import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LogoIcon } from '@/assets/Logo'
import {
  stagingTokenCopy,
  stagingTokenStorageKey,
  stagingTokenUserStorageKey,
  tokenAuthErrors,
} from './auth-mode'
import { DashboardAuthContext, type DashboardAuthValue, type DashboardUser } from './context'
import { validateStagingApiToken, verifyStagingToken } from './token-auth'
import { getApiErrorMessage } from '@/lib/api-error'

const TOKEN_AUTH_CHANGED_EVENT = 'usars-staging-token-auth-changed'

function safeReadStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeWriteStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Ignore locked-down storage; the login still succeeds for this render pass.
  }
}

function safeRemoveStorage(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Ignore locked-down storage.
  }
}

function dispatchTokenAuthChanged(): void {
  window.dispatchEvent(new Event(TOKEN_AUTH_CHANGED_EVENT))
}

function readStoredUser(): DashboardUser | null {
  const raw = safeReadStorage(stagingTokenUserStorageKey)
  if (!raw) return null
  try {
    return JSON.parse(raw) as DashboardUser
  } catch {
    safeRemoveStorage(stagingTokenUserStorageKey)
    return null
  }
}

export function TokenAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState(() => safeReadStorage(stagingTokenStorageKey))
  const [user, setUser] = useState<DashboardUser | null>(() => readStoredUser())
  const [isLoading, setIsLoading] = useState(false)

  const clearTokenSession = useCallback(() => {
    safeRemoveStorage(stagingTokenStorageKey)
    safeRemoveStorage(stagingTokenUserStorageKey)
    setToken(null)
    setUser(null)
    dispatchTokenAuthChanged()
  }, [])

  useEffect(() => {
    const syncFromStorage = () => {
      setToken(safeReadStorage(stagingTokenStorageKey))
      setUser(readStoredUser())
    }
    window.addEventListener('storage', syncFromStorage)
    window.addEventListener(TOKEN_AUTH_CHANGED_EVENT, syncFromStorage)
    return () => {
      window.removeEventListener('storage', syncFromStorage)
      window.removeEventListener(TOKEN_AUTH_CHANGED_EVENT, syncFromStorage)
    }
  }, [])

  const signIn = useCallback(async (rawToken?: string) => {
    const validatedToken = validateStagingApiToken(rawToken ?? '')
    setIsLoading(true)
    try {
      const verifiedUser = await verifyStagingToken(validatedToken)
      safeWriteStorage(stagingTokenStorageKey, validatedToken)
      safeWriteStorage(stagingTokenUserStorageKey, JSON.stringify(verifiedUser))
      setToken(validatedToken)
      setUser(verifiedUser)
      dispatchTokenAuthChanged()
    } finally {
      setIsLoading(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    clearTokenSession()
  }, [clearTokenSession])

  const getAccessToken = useCallback(async () => token, [token])

  const value = useMemo<DashboardAuthValue>(
    () => ({
      authMode: 'token',
      isAuthenticated: Boolean(token),
      isLoading,
      user,
      signIn,
      silentSignIn: async () => undefined,
      signOut,
      getAccessToken,
    }),
    [getAccessToken, isLoading, signIn, signOut, token, user],
  )

  return <DashboardAuthContext.Provider value={value}>{children}</DashboardAuthContext.Provider>
}

interface StagingTokenLoginCardProps {
  isLoading?: boolean
  onSubmit: (token: string) => Promise<void>
}

export function StagingTokenLoginCard({ isLoading = false, onSubmit }: StagingTokenLoginCardProps) {
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const submitting = isLoading || isSubmitting

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (submitting || isSubmittingRef.current) {
      return
    }

    setError(null)
    let validatedToken: string
    try {
      validatedToken = validateStagingApiToken(token)
    } catch (validationError) {
      setError(getApiErrorMessage(validationError, 'Enter a USA-RS API token.'))
      return
    }

    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      await onSubmit(validatedToken)
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : tokenAuthErrors.unavailable)
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-8">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <LogoIcon />
          </div>
          <CardTitle className="text-xl">USA-RS Dashboard</CardTitle>
          <CardDescription>
            <span className="block font-medium text-foreground">{stagingTokenCopy.eyebrow}</span>
            <span className="block">{stagingTokenCopy.description}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2 text-left">
              <Label htmlFor="staging-api-token">{stagingTokenCopy.tokenLabel}</Label>
              <Input
                id="staging-api-token"
                value={token}
                type="password"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={stagingTokenCopy.tokenPlaceholder}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'staging-token-error' : undefined}
                onChange={(event) => setToken(event.currentTarget.value)}
                disabled={submitting}
              />
            </div>
            {error && (
              <p id="staging-token-error" role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button className="w-full" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                stagingTokenCopy.submit
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
